import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { checkAndHandleAuthError, handleAuthError } from '../../utils/authErrorHandler';
import api from '../../utils/request';

// API基础URL
// 使用相对路径，通过Nginx代理访问后端API
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// 静默刷新 access token
async function refreshAccessTokenOnce() {
  try {
    const resp = await api.post('/auth/refresh', {}, {
      withCredentials: true
    });
    if (resp.data?.success && resp.data?.access_token) {
      localStorage.setItem('access_token', resp.data.access_token);
      return resp.data.access_token;
    }
    return null;
  } catch (error) {
    console.warn('刷新令牌异常:', error);
    if (error.response?.status === 401) {
      handleAuthError('登录已过期，请重新登录', false);
    }
    return null;
  }
}

// 轻量级颜色处理：让同色系更有层次感
const lightenHex = (hex, amt = 12) => {
  try {
    const clean = hex.replace('#', '');
    const num = parseInt(clean.length === 3 ? clean.split('').map(c => c + c).join('') : clean, 16);
    let r = (num >> 16) + amt;
    let g = ((num >> 8) & 0x00ff) + amt;
    let b = (num & 0x0000ff) + amt;
    r = Math.max(0, Math.min(255, r));
    g = Math.max(0, Math.min(255, g));
    b = Math.max(0, Math.min(255, b));
    const toHex = (v) => v.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  } catch (_) {
    return hex;
  }
};

// 数据颜色配置 - 朋克风格霓虹色系
const dataColors = {
  '职位搜索': '#5B8EFF',  // 霓虹电蓝
  '简历诊断': '#A855F7',   // 霓虹紫
  '面试押题': '#EC4899',   // 霓虹粉
  '模拟面试': '#F97316',   // 霓虹橙红
  '会议助手': '#06B6D4'    // 霓虹青
};

// 职场动态数据将从API获取
// eslint-disable-next-line no-unused-vars
const weeklyTrendData = [
  { week: '第1周', 职位搜索: 35, 简历诊断: 8, 面试押题: 12, 模拟面试: 3, 会议助手: 7 },
  { week: '第2周', 职位搜索: 42, 简历诊断: 6, 面试押题: 15, 模拟面试: 4, 会议助手: 8 },
  { week: '第3周', 职位搜索: 38, 简历诊断: 5, 面试押题: 10, 模拟面试: 2, 会议助手: 6 },
  { week: '第4周', 职位搜索: 41, 简历诊断: 4, 面试押题: 8, 模拟面试: 3, 会议助手: 7 }
];

const JobDataCharts = ({ onNavigate }) => {
  // 本月统计数据状态管理
  const [monthlyData, setMonthlyData] = useState([
    { name: '职位搜索', value: 0, color: dataColors['职位搜索'] },
    { name: '简历诊断', value: 0, color: dataColors['简历诊断'] },
    { name: '面试押题', value: 0, color: dataColors['面试押题'] },
    { name: '模拟面试', value: 0, color: dataColors['模拟面试'] },
    { name: '会议助手', value: 0, color: dataColors['会议助手'] }
  ]);
  const [statsLoading, setStatsLoading] = useState(true);
  
  // 存储被隐藏的数据项
  const [hiddenItems, setHiddenItems] = useState([]);
  
  // 处理图例点击事件
  const handleLegendClick = (entry) => {
    setHiddenItems(prev => {
      if (prev.includes(entry.name)) {
        // 如果已隐藏，则显示
        return prev.filter(item => item !== entry.name);
      } else {
        // 如果已显示，则隐藏
        return [...prev, entry.name];
      }
    });
  };
  
  // 职场动态状态管理
  const [newsData, setNewsData] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);
  
  // 分页状态管理
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  
  // 获取本月统计数据
  useEffect(() => {
    const fetchMonthlyStats = async () => {
      try {
        setStatsLoading(true);
        const response = await api.get('/user-stats/monthly-stats');
        
        if (response.data.success && response.data.data) {
          const stats = response.data.data;
          const monthlyDataArray = [
            { name: '职位搜索', value: stats.job_search || 0, color: dataColors['职位搜索'] },
            { name: '简历诊断', value: stats.resume_diagnosis || 0, color: dataColors['简历诊断'] },
            { name: '面试押题', value: stats.interview_prep || 0, color: dataColors['面试押题'] },
            { name: '模拟面试', value: stats.mock_interview || 0, color: dataColors['模拟面试'] },
            { name: '会议助手', value: stats.meeting_assistant || 0, color: dataColors['会议助手'] }
          ];
          setMonthlyData(monthlyDataArray);
        } else {
          console.warn('API返回数据格式不正确:', response.data);
        }
      } catch (error) {
        console.error('获取本月统计数据失败:', error);
        if (error.response?.status === 401) {
          checkAndHandleAuthError(error.response, true);
        }
      } finally {
        setStatsLoading(false);
      }
    };
    
    fetchMonthlyStats();
  }, []);
  
  // 获取职场动态数据
  useEffect(() => {
    const fetchNewsData = async () => {
      try {
        const response = await api.get('/workplace-news/?status=published&page_size=20');
        
        if (response.data.success && response.data.data && response.data.data.length > 0) {
          const formattedNews = response.data.data.map(news => ({
            id: news.id,
            news_id: news.news_id,
            title: news.title,
            content: news.content,
            summary: news.summary,
            category: news.category,
            time: formatTimeAgo(news.published_at || news.created_at),
            published_at: news.published_at,
            created_at: news.created_at,
            view_count: news.view_count || 0
          }));
          setNewsData(formattedNews);
        } else {
          setNewsData([]);
        }
      } catch (error) {
        console.error('获取职场动态失败:', error);
        setNewsData([]);
      } finally {
        setNewsLoading(false);
      }
    };
    
    fetchNewsData();
  }, []);

  // 处理点击动态项
  const handleNewsClick = (news) => {
    // 优先使用news_id，如果没有则使用id
    const newsId = news.news_id || news.id;
    window.open(`/workplace-news/${newsId}`, '_blank');
  };
  
  // 格式化时间为年月日
  const formatTimeAgo = (dateString) => {
    if (!dateString) return '未知时间';
    
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  };
  
  // 计算总页数
  const totalPages = Math.ceil(newsData.length / itemsPerPage);
  
  // 获取当前页的数据
  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return newsData.slice(startIndex, endIndex);
  };
  
  // 分页处理函数
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };
  // eslint-disable-next-line no-unused-vars
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <TooltipContainer>
          <p style={{ margin: 0, fontWeight: 600, color: '#2c3e50' }}>{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ margin: '4px 0', color: entry.color }}>
              {`${entry.dataKey}: ${entry.value}次`}
            </p>
          ))}
        </TooltipContainer>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const total = monthlyData.reduce((sum, item) => sum + item.value, 0);
      const percentage = total > 0 ? ((data.value / total) * 100).toFixed(1) : 0;
      return (
        <TooltipContainer>
          <p style={{ margin: 0, fontWeight: 600, color: data.payload.color }}>
            {data.name}: {data.value}次
          </p>
          {total > 0 && (
            <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#6c757d' }}>
              占比: {percentage}%
            </p>
          )}
        </TooltipContainer>
      );
    }
    return null;
  };

  // 自定义图例：霓虹渐变小圆点 + 标签（朋克风格）
  const renderLegend = () => {
    return (
      <LegendWrapper>
        {monthlyData.map((item, idx) => {
          const isHidden = hiddenItems.includes(item.name);
          return (
            <LegendItem 
              key={item.name}
              onClick={() => handleLegendClick(item)}
              style={{ cursor: 'pointer', opacity: isHidden ? 0.6 : 1 }}
            >
              <LegendDot style={{
                background: isHidden 
                  ? '#CBD5E1' 
                  : `radial-gradient(circle at 30% 30%, ${lightenHex(item.color, 60)} 0%, ${item.color} 70%, ${item.color}DD 100%)`,
                boxShadow: isHidden ? 'none' : `0 2px 4px ${item.color}30, inset 0 1px 2px rgba(255,255,255,0.4)`,
                border: isHidden ? '1px solid #E2E8F0' : `1px solid ${item.color}60`,
                filter: isHidden ? 'grayscale(1)' : 'none'
              }} />
              <span className="label" style={{ textDecoration: isHidden ? 'line-through' : 'none' }}>{item.name}</span>
            </LegendItem>
          );
        })}
      </LegendWrapper>
    );
  };

  return (
    <ChartsContainer>
      <ChartSection>
        <ChartTitle>本月求职</ChartTitle>
        <ChartWrapper>
          {statsLoading ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#718096' }}>
              加载中...
            </div>
          ) : (() => {
            const total = monthlyData.reduce((sum, item) => sum + item.value, 0);
            if (total === 0) {
              return (
                <EmptyStateWrapper>
                  {/* 背景装饰元素 - 左侧 */}
                  <div style={{
                    position: 'absolute',
                    left: '-20px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, rgba(91, 142, 255, 0.08), rgba(168, 85, 247, 0.05))',
                    filter: 'blur(20px)',
                    animation: 'emptyStatePulse 4s ease-in-out infinite'
                  }} />
                  
                  {/* 背景装饰元素 - 右侧 */}
                  <div style={{
                    position: 'absolute',
                    right: '-20px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.08), rgba(249, 115, 22, 0.05))',
                    filter: 'blur(20px)',
                    animation: 'emptyStatePulse 4s ease-in-out infinite 0.5s'
                  }} />
                  
                  <EmptyStateSVG viewBox="0 0 200 200" preserveAspectRatio="xMidYMid meet">
                    <defs>
                      {/* 主渐变 - 蓝紫色 */}
                      <linearGradient id="mainGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#5B8EFF" stopOpacity="0.9" />
                        <stop offset="100%" stopColor="#A855F7" stopOpacity="0.7" />
                      </linearGradient>
                      {/* 辅助渐变 */}
                      <linearGradient id="accentGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#EC4899" stopOpacity="0.7" />
                        <stop offset="100%" stopColor="#F97316" stopOpacity="0.5" />
                      </linearGradient>
                      {/* 背景圆环渐变 */}
                      <linearGradient id="bgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#E0E7FF" />
                        <stop offset="100%" stopColor="#F3E8FF" />
                      </linearGradient>
                    </defs>
                    
                    {/* 外层装饰圆环 */}
                    <circle
                      cx="100"
                      cy="100"
                      r="90"
                      fill="none"
                      stroke="url(#bgGradient)"
                      strokeWidth="1"
                      opacity="0.4"
                    />
                    
                    {/* 背景装饰圆 */}
                    <circle
                      cx="100"
                      cy="100"
                      r="75"
                      fill="url(#bgGradient)"
                      opacity="0.25"
                      style={{
                        animation: 'emptyStatePulse 3s ease-in-out infinite'
                      }}
                    />
                    
                    {/* 空环形图 - 外圈（灰色底） */}
                    <circle
                      cx="100"
                      cy="100"
                      r="65"
                      fill="none"
                      stroke="#E5E7EB"
                      strokeWidth="16"
                      opacity="0.4"
                    />
                    
                    {/* 空环形图 - 渐变装饰弧线1 */}
                    <circle
                      cx="100"
                      cy="100"
                      r="65"
                      fill="none"
                      stroke="url(#mainGradient)"
                      strokeWidth="16"
                      strokeDasharray="80 330"
                      strokeLinecap="round"
                      opacity="0.9"
                      style={{
                        animation: 'emptyStateRotate 4s ease-in-out infinite',
                        transformOrigin: '100px 100px'
                      }}
                    />
                    
                    {/* 空环形图 - 渐变装饰弧线2 */}
                    <circle
                      cx="100"
                      cy="100"
                      r="65"
                      fill="none"
                      stroke="url(#accentGradient)"
                      strokeWidth="16"
                      strokeDasharray="60 350"
                      strokeDashoffset="200"
                      strokeLinecap="round"
                      opacity="0.7"
                      style={{
                        animation: 'emptyStateRotate 5s ease-in-out infinite reverse',
                        transformOrigin: '100px 100px'
                      }}
                    />
                    
                    {/* 中心图标容器 - 圆形背景 */}
                    <circle
                      cx="100"
                      cy="100"
                      r="42"
                      fill="white"
                      opacity="0.95"
                      style={{
                        filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.08))'
                      }}
                    />
                    
                    {/* 柱状图图标 - 放大 */}
                    <g transform="translate(100, 100)">
                      {/* 第一根柱子 */}
                      <rect
                        x="-26"
                        y="-10"
                        width="12"
                        height="20"
                        rx="3"
                        fill="url(#mainGradient)"
                        opacity="0.6"
                        style={{
                          animation: 'emptyStateFloat 2s ease-in-out infinite'
                        }}
                      />
                      {/* 第二根柱子 */}
                      <rect
                        x="-7"
                        y="-18"
                        width="12"
                        height="28"
                        rx="3"
                        fill="url(#mainGradient)"
                        opacity="0.8"
                        style={{
                          animation: 'emptyStateFloat 2s ease-in-out infinite 0.2s'
                        }}
                      />
                      {/* 第三根柱子 */}
                      <rect
                        x="12"
                        y="-6"
                        width="12"
                        height="16"
                        rx="3"
                        fill="url(#mainGradient)"
                        opacity="0.6"
                        style={{
                          animation: 'emptyStateFloat 2s ease-in-out infinite 0.4s'
                        }}
                      />
                      
                      {/* 空状态标识 */}
                      <text
                        x="0"
                        y="24"
                        textAnchor="middle"
                        fill="#9CA3AF"
                        fontSize="14"
                        fontWeight="500"
                        opacity="0.7"
                      >
                        • • •
                      </text>
                    </g>
                    
                    {/* 装饰元素 - 小圆点（增加数量） */}
                    {[
                      { x: 40, y: 40, delay: 0 },
                      { x: 160, y: 40, delay: 0.3 },
                      { x: 40, y: 160, delay: 0.6 },
                      { x: 160, y: 160, delay: 0.9 },
                      { x: 100, y: 20, delay: 1.2 },
                      { x: 20, y: 100, delay: 1.5 },
                      { x: 180, y: 100, delay: 1.8 },
                      { x: 100, y: 180, delay: 2.1 }
                    ].map((pos, i) => (
                      <circle
                        key={i}
                        cx={pos.x}
                        cy={pos.y}
                        r="2.5"
                        fill={i % 2 === 0 ? "url(#mainGradient)" : "url(#accentGradient)"}
                        opacity="0.5"
                        style={{
                          animation: `emptyStateFloat 3s ease-in-out infinite ${pos.delay}s`
                        }}
                      />
                    ))}
                  </EmptyStateSVG>
                  
                  <EmptyStateText>
                    <EmptyStateTitle>还未使用任何功能</EmptyStateTitle>
                    <EmptyStateSubtitle>开始使用功能后，数据将在这里显示</EmptyStateSubtitle>
                  </EmptyStateText>
                  
                  <style>{`
                    @keyframes emptyStateRotate {
                      0%, 100% { 
                        transform: rotate(0deg);
                      }
                      50% { 
                        transform: rotate(180deg);
                      }
                    }
                    @keyframes emptyStatePulse {
                      0%, 100% { 
                        opacity: 0.25;
                        transform: scale(0.98);
                      }
                      50% { 
                        opacity: 0.5;
                        transform: scale(1.02);
                      }
                    }
                    @keyframes emptyStateFloat {
                      0%, 100% { 
                        transform: translateY(0);
                        opacity: 0.5;
                      }
                      50% { 
                        transform: translateY(-3px);
                        opacity: 0.8;
                      }
                    }
                  `}</style>
                </EmptyStateWrapper>
              );
            }
            return (
              <ChartContainer>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                  <defs>
                    {monthlyData.map((entry, index) => (
                      <linearGradient key={`sliceGrad-${index}`} id={`sliceGrad-${index}`} x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor={lightenHex(entry.color, 40)} stopOpacity="1" />
                        <stop offset="50%" stopColor={entry.color} stopOpacity="1" />
                        <stop offset="100%" stopColor={entry.color} stopOpacity="0.9" />
                      </linearGradient>
                    ))}
                  </defs>
                  <Pie
                    data={monthlyData.filter(item => item.value > 0 && !hiddenItems.includes(item.name))}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={80}
                    paddingAngle={4}
                    dataKey="value"
                    stroke="rgba(255,255,255,0.5)"
                    strokeWidth={1.5}
                  >
                    {monthlyData.filter(item => item.value > 0 && !hiddenItems.includes(item.name)).map((entry, index) => {
                      const originalIndex = monthlyData.findIndex(item => item.name === entry.name);
                      return (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={`url(#sliceGrad-${originalIndex})`}
                        />
                      );
                    })}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                  <Legend 
                    verticalAlign="bottom" 
                    height={40}
                    content={renderLegend}
                    wrapperStyle={{ paddingTop: '10px' }}
                  />
                  </PieChart>
                </ResponsiveContainer>
              </ChartContainer>
            );
          })()}
        </ChartWrapper>
      </ChartSection>

      <ChartSection>
        <ChartTitle>职场动态</ChartTitle>
        <NewsWrapper>
          {newsLoading ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#718096' }}>
              加载中...
            </div>
          ) : (
            <>
              <NewsList>
                {getCurrentPageData().map((item, index) => (
                  <NewsItem 
                    key={item.id} 
                    isLast={index === getCurrentPageData().length - 1 && totalPages <= 1}
                    onClick={() => handleNewsClick(item)}
                  >
                    <NewsIndicator />
                    <NewsContent>
                      <NewsTitle>{item.title}</NewsTitle>
                      <NewsTime>{item.time}</NewsTime>
                    </NewsContent>
                  </NewsItem>
                ))}
              </NewsList>
              
              {/* 分页组件 */}
              {totalPages > 1 && (
                <PaginationWrapper>
                  <PaginationButton 
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ArrowIcon>‹</ArrowIcon>
                  </PaginationButton>
                  
                  <PageText>
                    第 {currentPage} 页/共 {totalPages} 页
                  </PageText>
                  
                  <PaginationButton 
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <ArrowIcon>›</ArrowIcon>
                  </PaginationButton>
                </PaginationWrapper>
              )}
            </>
          )}
        </NewsWrapper>
      </ChartSection>

    </ChartsContainer>
  );
};

const ChartsContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 30px 20px 20px 20px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  
  /* 大屏幕优化 */
  @media (min-width: 1920px) {
    padding: 36px 28px 24px 28px;
    border-radius: 20px;
  }
  
  /* 超大屏幕优化 */
  @media (min-width: 2560px) {
    padding: 40px 32px 28px 32px;
    border-radius: 24px;
  }
  
  /* 小屏幕 */
  @media (max-width: 768px) {
    padding: 24px 16px 16px 16px;
    border-radius: 12px;
  }
`;

const ChartSection = styled.div`
  margin-bottom: 20px;
  
  &:last-of-type {
    margin-bottom: 0;
    margin-top: 15px;
  }
  
  /* 大屏幕优化 */
  @media (min-width: 1920px) {
    margin-bottom: 28px;
    
    &:last-of-type {
      margin-top: 20px;
    }
  }
  
  /* 超大屏幕优化 */
  @media (min-width: 2560px) {
    margin-bottom: 32px;
    
    &:last-of-type {
      margin-top: 24px;
    }
  }
`;

const ChartTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 16px 0;
  text-align: center;
  
  /* 大屏幕优化 */
  @media (min-width: 1920px) {
    font-size: 18px;
    margin: 0 0 20px 0;
  }
  
  /* 超大屏幕优化 */
  @media (min-width: 2560px) {
    font-size: 20px;
    margin: 0 0 24px 0;
  }
`;

const ChartWrapper = styled.div`
  background: linear-gradient(135deg, #fafbfc 0%, #f5f7fa 100%);
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #f0f0f0;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.02);
  
  /* 大屏幕优化 */
  @media (min-width: 1920px) {
    padding: 20px;
    border-radius: 16px;
  }
  
  /* 超大屏幕优化 */
  @media (min-width: 2560px) {
    padding: 24px;
    border-radius: 20px;
  }
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 200px;
  
  /* 大屏幕优化 */
  @media (min-width: 1920px) {
    height: 240px;
  }
  
  /* 超大屏幕优化 */
  @media (min-width: 2560px) {
    height: 280px;
  }
  
  .recharts-responsive-container {
    width: 100% !important;
    height: 100% !important;
  }
`;

const TooltipContainer = styled.div`
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-size: 13px;
`;



export default JobDataCharts;

// 自定义图例样式
const LegendWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
  padding: 16px 8px 4px 8px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #334155;
`;

const LegendDot = styled.span`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  display: inline-block;
  position: relative;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.2);
    filter: brightness(1.2);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: 3px;
    width: 4px;
    height: 4px;
    background: rgba(255, 255, 255, 0.6);
    border-radius: 50%;
    filter: blur(1px);
  }
`;

// 新增：优化后的消息栏样式
const NewsWrapper = styled.div`
  background: #fafbfc;
  border-radius: 12px;
  padding: 6px;
  border: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  min-height: fit-content;
  
  /* 大屏幕优化 */
  @media (min-width: 1920px) {
    padding: 8px;
    border-radius: 16px;
  }
  
  /* 超大屏幕优化 */
  @media (min-width: 2560px) {
    padding: 10px;
    border-radius: 20px;
  }
`;

const NewsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: fit-content;
`;

const NewsItem = styled.li`
  display: flex;
  align-items: flex-start;
  padding: 12px 14px;
  margin-bottom: ${props => props.isLast ? '0' : '1px'};
  background: white;
  border-radius: 8px;
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;

  &:hover {
    background-color: #f8fafc;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
  
  /* 大屏幕优化 */
  @media (min-width: 1920px) {
    padding: 14px 16px;
    border-radius: 10px;
  }
  
  /* 超大屏幕优化 */
  @media (min-width: 2560px) {
    padding: 16px 18px;
    border-radius: 12px;
  }
`;

const NewsIndicator = styled.div`
  width: 6px;
  height: 6px;
  background: radial-gradient(circle at 30% 30%, #A855F7, #5B8EFF);
  border-radius: 50%;
  margin-top: 6px;
  margin-right: 10px;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(168, 85, 247, 0.3);
  animation: pulse 2s ease-in-out infinite;
  
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.8;
      transform: scale(0.95);
    }
  }
`;

const NewsContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

const NewsTitle = styled.div`
  font-size: 13px !important;
  font-weight: 500;
  color: #1f2937;
  line-height: 1.4;
  flex: 1;
  margin-right: 8px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  
  /* 大屏幕优化 */
  @media (min-width: 1920px) {
    font-size: 14px !important;
    line-height: 1.5;
    margin-right: 12px;
  }
  
  /* 超大屏幕优化 */
  @media (min-width: 2560px) {
    font-size: 15px !important;
    line-height: 1.6;
    margin-right: 16px;
  }
`;

const NewsTime = styled.div`
  font-size: 11px !important;
  color: #6b7280 !important;
  font-weight: 400 !important;
  line-height: 1.4 !important;
  font-family: inherit !important;
  flex-shrink: 0;
  margin-top: 1px;
  min-width: 85px;
  text-align: left;
  white-space: nowrap;
  
  /* 大屏幕优化 */
  @media (min-width: 1920px) {
    font-size: 12px !important;
    min-width: 90px;
  }
  
  /* 超大屏幕优化 */
  @media (min-width: 2560px) {
    font-size: 13px !important;
    min-width: 95px;
  }
`;

// 分页组件样式
const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
  padding: 6px 0 0 0;
  border-top: 1px solid #f0f0f0;
  
  /* 大屏幕优化 */
  @media (min-width: 1920px) {
    margin-top: 12px;
    padding: 8px 0 0 0;
    gap: 16px;
  }
  
  /* 超大屏幕优化 */
  @media (min-width: 2560px) {
    margin-top: 14px;
    padding: 10px 0 0 0;
    gap: 20px;
  }
`;

const PaginationButton = styled.button`
  width: 32px;
  height: 32px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: ${props => props.disabled ? '#f5f5f5' : 'white'};
  color: ${props => props.disabled ? '#9ca3af' : '#374151'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover:not(:disabled) {
    background: #f9fafb;
    border-color: #d1d5db;
  }
`;

const ArrowIcon = styled.span`
  font-size: 18px;
  font-weight: bold;
  line-height: 1;
`;

const PageText = styled.span`
  font-size: 13px;
  color: #374151;
  font-weight: 500;
  white-space: nowrap;
`;

// 空状态组件样式
const EmptyStateWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  height: 200px;
  padding: 20px;
  text-align: center;
  box-sizing: border-box;
  gap: 24px;
  position: relative;
  overflow: hidden;
`;

const EmptyStateSVG = styled.svg`
  width: 160px;
  height: 160px;
  filter: drop-shadow(0 4px 16px rgba(91, 142, 255, 0.2));
  flex-shrink: 0;
`;

const EmptyStateText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
  flex-shrink: 0;
  z-index: 1;
`;

const EmptyStateTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #6366F1;
  margin: 0;
  letter-spacing: -0.01em;
  line-height: 1.5;
`;

const EmptyStateSubtitle = styled.div`
  font-size: 13px;
  color: #94A3B8;
  font-weight: 400;
  margin: 0;
  line-height: 1.6;
  letter-spacing: 0.01em;
`;